// src/components/admin/views/TotalTasksView.tsx

import React, {
    useState,
    useEffect,
    useMemo,
    useCallback,
    useRef,
    useDeferredValue,
  } from 'react';
  import {
    BarChart3, Users, Target, Filter, Download,
    ChevronDown, Search, Award, Zap, CheckCircle,
    RefreshCw, Database, Star
  } from 'lucide-react';
  import EnhancedStatCard from '../components/EnhancedStatCard';
  import { focalApiService } from '../../../services/focalApiService';
  import {
    AgentPerformanceStats,
    ProcessedAgentTaskStats,
  } from '../types/AdminTypes';
  
  // Types pour les filtres appliqués à l'affichage (client)
  interface UiFilters {
    selectedAgents: string[];
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  }
  
  // Types pour les dates de requête (serveur)
  interface QueryParams {
    dateFrom: string;
    dateTo: string;
  }
  
  // Type combiné pour l'affichage
  interface CombinedAgentStats extends ProcessedAgentTaskStats {
    performance?: AgentPerformanceStats;
  }
  
  const PAGE_SIZE = 25;
  
  const TotalTasksView: React.FC = () => {
    // États principaux data
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [taskStats, setTaskStats] = useState<ProcessedAgentTaskStats[]>([]);
    const [performanceStats, setPerformanceStats] = useState<AgentPerformanceStats[]>([]);
  
    // Panneau filtres
    const [showFilters, setShowFilters] = useState(false);
  
    // ---- Gestion "propre" : brouillon (UI) vs appliqué (data + tri) ----
    const initialDateFrom = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString().split('T')[0];
    const initialDateTo = new Date().toISOString().split('T')[0];
  
    // Brouillon contrôlé par les inputs
    const [draftQuery, setDraftQuery] = useState<QueryParams>({
      dateFrom: initialDateFrom,
      dateTo: initialDateTo,
    });
  
    const [draftUiFilters, setDraftUiFilters] = useState<UiFilters>({
      selectedAgents: [],
      // Par défaut : demande “ordre croissant sur email”
      sortBy: 'email',
      sortOrder: 'asc',
    });
  
    // États appliqués (ce que l’on utilise vraiment pour afficher/fetcher)
    const [queryParams, setQueryParams] = useState<QueryParams>({
      dateFrom: initialDateFrom,
      dateTo: initialDateTo,
    });
  
    const [uiFilters, setUiFilters] = useState<UiFilters>({
      selectedAgents: [],
      sortBy: 'email',
      sortOrder: 'asc',
    });
  
    // Recherche côté client
    const [agentSearch, setAgentSearch] = useState('');
    const deferredAgentSearch = useDeferredValue(agentSearch);
  
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
  
    // AbortController pour rendre les chargements plus "rapides" (on annule l’ancien)
    const abortRef = useRef<AbortController | null>(null);
  
    // Chargement des données — déclenché UNIQUEMENT quand queryParams change (après clic)
    const loadData = useCallback(async () => {
      setLoading(true);
      setError(null);
  
      // Annule toute requête précédente
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
  
      try {
        // focalApiService peut accepter signal si vous le passez plus loin (si non, au moins on évite setState après abort)
        const { taskStats: tasksData, performanceStats: performanceData } =
          await focalApiService.getCombinedTasksAndPerformanceStats({
            dateFrom: queryParams.dateFrom,
            dateTo: queryParams.dateTo,
            // @ts-ignore: passez le signal si votre service le supporte
            signal: controller.signal,
          });
  
        setTaskStats(tasksData);
        setPerformanceStats(performanceData);
        setCurrentPage(1); // on revient à la page 1 après un nouveau fetch
  
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError(err?.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    }, [queryParams.dateFrom, queryParams.dateTo]);
  
    useEffect(() => {
      loadData();
    }, [loadData]);
  
    // Appliquer filtres & dates (sans auto-refresh pendant la saisie)
    const applyFilters = () => {
      setUiFilters(draftUiFilters);
      setQueryParams(draftQuery); // déclenche la récupération serveur (loadData via useEffect)
    };
  
    // Tri e-mail intelligent : agent-1 < agent-2 < agent-10 …
    const cmpEmailSmart = (aEmail: string, bEmail: string) => {
      const aMatch = aEmail.match(/agent-(\d+)/i);
      const bMatch = bEmail.match(/agent-(\d+)/i);
      if (aMatch && bMatch) {
        const aNum = parseInt(aMatch[1], 10);
        const bNum = parseInt(bMatch[1], 10);
        if (aNum !== bNum) return aNum - bNum;
        return aEmail.localeCompare(bEmail);
      }
      return aEmail.localeCompare(bEmail);
    };
  
    // Données combinées et filtrées (client)
    const combinedData = useMemo(() => {
      const combined: CombinedAgentStats[] = taskStats.map(taskStat => ({
        ...taskStat,
        performance: performanceStats.find(perf => perf.email === taskStat.email),
      }));
  
      // Filtrage par agents sélectionnés (si vous ajoutez un multi-select plus tard)
      let filtered = uiFilters.selectedAgents.length > 0
        ? combined.filter(item => uiFilters.selectedAgents.includes(item.email))
        : combined;
  
      // Filtrage recherche (déférée pour perf)
      if (deferredAgentSearch.trim()) {
        const q = deferredAgentSearch.toLowerCase();
        filtered = filtered.filter(item => item.email.toLowerCase().includes(q));
      }
  
      // Tri
      filtered.sort((a, b) => {
        let aValue: number, bValue: number;
  
        switch (uiFilters.sortBy) {
          case 'total_draw_tasks':
            aValue = a.total_draw_tasks; bValue = b.total_draw_tasks; break;
          case 'total_match_draw_edit_tasks':
            aValue = a.total_match_draw_edit_tasks; bValue = b.total_match_draw_edit_tasks; break;
          case 'total_pair_tasks':
            aValue = a.total_pair_tasks; bValue = b.total_pair_tasks; break;
          case 'pair_only_tasks':
            aValue = a.pair_only_tasks; bValue = b.pair_only_tasks; break;
          case 'total_review_tasks':
            aValue = a.total_review_tasks; bValue = b.total_review_tasks; break;
          case 'total_average_f1':
            aValue = a.performance?.total_average_f1 || 0;
            bValue = b.performance?.total_average_f1 || 0;
            break;
          case 'email': {
            const res = cmpEmailSmart(a.email, b.email);
            return uiFilters.sortOrder === 'asc' ? res : -res;
          }
          default:
            aValue = a.total_all_tasks; bValue = b.total_all_tasks;
        }
  
        return uiFilters.sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      });
  
      return filtered;
    }, [taskStats, performanceStats, uiFilters, deferredAgentSearch]);
  
    // Pagination (25 / page) + scroll intérieur
    const totalRows = combinedData.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));
    const paginatedData = useMemo(() => {
      const start = (currentPage - 1) * PAGE_SIZE;
      return combinedData.slice(start, start + PAGE_SIZE);
    }, [combinedData, currentPage]);
  
    // Stats globales
    const globalStats = useMemo(() => {
      const stats = combinedData.reduce((acc, agent) => {
        acc.totalAgents++;
        acc.totalDrawTasks += agent.total_draw_tasks;
        acc.totalEditDrawTasks += agent.total_match_draw_edit_tasks;
        acc.totalPairFullTasks += agent.total_pair_tasks;
        acc.totalPairOnlyTasks += agent.pair_only_tasks;
        acc.totalReviewTasks += agent.total_review_tasks;
        acc.totalAllTasks += agent.total_all_tasks;
  
        if (agent.performance) {
          acc.totalWithPerformance++;
          acc.avgF1Score += agent.performance.total_average_f1;
          acc.avgDrawScore += agent.performance.average_draw_score;
          acc.avgPairScore += agent.performance.average_pair_score;
        }
        return acc;
      }, {
        totalAgents: 0,
        totalDrawTasks: 0,
        totalEditDrawTasks: 0,
        totalPairFullTasks: 0,
        totalPairOnlyTasks: 0,
        totalReviewTasks: 0,
        totalAllTasks: 0,
        totalWithPerformance: 0,
        avgF1Score: 0,
        avgDrawScore: 0,
        avgPairScore: 0,
      });
  
      if (stats.totalWithPerformance > 0) {
        stats.avgF1Score /= stats.totalWithPerformance;
        stats.avgDrawScore /= stats.totalWithPerformance;
        stats.avgPairScore /= stats.totalWithPerformance;
      }
      return stats;
    }, [combinedData]);
  
    // Handlers pour le brouillon (pas d’effet secondaire)
    const handleDraftChange = (key: keyof UiFilters, value: any) => {
      setDraftUiFilters(prev => ({ ...prev, [key]: value }));
    };
    const handleDraftDateChange = (key: keyof QueryParams, value: string) => {
      setDraftQuery(prev => ({ ...prev, [key]: value }));
    };
  
    // Export CSV (conservé)
    const exportCSV = useCallback(() => {
      const csvData = combinedData.map(agent => ({
        'Email': agent.email,
        'Total Draw': agent.total_draw_tasks,
        'Total Edit Draw': agent.total_match_draw_edit_tasks,
        'Total Pair Full': agent.total_pair_tasks,
        'Total Pair Only': agent.pair_only_tasks,
        'Total Review': agent.total_review_tasks,
        'Total All Tasks': agent.total_all_tasks,
        'Avg Draw Score': agent.performance?.average_draw_score?.toFixed(2) || 'N/A',
        'Avg Pair Score': agent.performance?.average_pair_score?.toFixed(2) || 'N/A',
        'Total Avg F1': agent.performance?.total_average_f1?.toFixed(2) || 'N/A'
      }));
  
      if (csvData.length === 0) return;
  
      const header = Object.keys(csvData[0]).join(',');
      const rows = csvData.map(row =>
        Object.values(row)
          .map(v => typeof v === 'string' && v.includes(',') ? `"${v}"` : v)
          .join(',')
      );
      const csv = [header, ...rows].join('\n');
  
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `total-tasks-report-${queryParams.dateFrom}-${queryParams.dateTo}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, [combinedData, queryParams.dateFrom, queryParams.dateTo]);
  
    // Export XLS “bien détaillé” (sans lib externe) via HTML table Excel-compatible
    const exportXLS = useCallback(() => {
      const headers = [
        'Email',
        'Total Draw',
        'Total Edit Draw',
        'Total Pair Full',
        'Total Pair Only',
        'Total Review',
        'Total All Tasks',
        'Avg Draw Score',
        'Avg Pair Score',
        'Total Avg F1'
      ];
  
      const rowsHtml = combinedData.map(agent => `
        <tr>
          <td>${agent.email}</td>
          <td>${agent.total_draw_tasks}</td>
          <td>${agent.total_match_draw_edit_tasks}</td>
          <td>${agent.total_pair_tasks}</td>
          <td>${agent.pair_only_tasks}</td>
          <td>${agent.total_review_tasks}</td>
          <td>${agent.total_all_tasks}</td>
          <td>${agent.performance ? agent.performance.average_draw_score.toFixed(2) : 'N/A'}</td>
          <td>${agent.performance ? agent.performance.average_pair_score.toFixed(2) : 'N/A'}</td>
          <td>${agent.performance ? agent.performance.total_average_f1.toFixed(2) : 'N/A'}</td>
        </tr>
      `).join('');
  
      const tableHtml = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head><!--[if gte mso 9]>
          <xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
            <x:Name>TotalTasks</x:Name>
            <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
          </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml>
        <![endif]--></head>
        <body>
          <table border="1">
            <thead>
              <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </body>
        </html>
      `.trim();
  
      const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `total-tasks-report-${queryParams.dateFrom}-${queryParams.dateTo}.xls`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, [combinedData, queryParams.dateFrom, queryParams.dateTo]);
  
    // UI
    if (loading) {
      return (
        <div className="space-y-6 sm:space-y-8">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement des données</h3>
              <p className="text-gray-600">Récupération des statistiques depuis le dashboard Focal...</p>
            </div>
          </div>
        </div>
      );
    }
  
    return (
      <div className="space-y-6 sm:space-y-8">
        {/* Header avec filtres */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Total Tasks Dashboard
              </h1>
              <p className="text-gray-600">
                Statistiques complètes des tâches par agent • {globalStats.totalAgents} agents actifs • {globalStats.totalAllTasks.toLocaleString()} tâches totales
              </p>
            </div>
  
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors"
              >
                <Filter className="w-4 h-4" />
                Filtres
                <ChevronDown className={`w-4 h-4 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
  
              <button
                onClick={applyFilters}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Appliquer
              </button>
  
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-xl hover:bg-purple-100 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
  
              <button
                onClick={exportXLS}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export XLS
              </button>
            </div>
          </div>
  
          {/* Panneau de filtres */}
          {showFilters && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Filtres de date (brouillon) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
                  <input
                    type="date"
                    value={draftQuery.dateFrom}
                    onChange={(e) => handleDraftDateChange('dateFrom', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
  
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
                  <input
                    type="date"
                    value={draftQuery.dateTo}
                    onChange={(e) => handleDraftDateChange('dateTo', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
  
                {/* Tri (brouillon) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trier par</label>
                  <select
                    value={draftUiFilters.sortBy}
                    onChange={(e) => handleDraftChange('sortBy', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="email">Email</option>
                    <option value="total_all_tasks">Total Tâches</option>
                    <option value="total_draw_tasks">Draw Tasks</option>
                    <option value="total_match_draw_edit_tasks">Edit Draw Tasks</option>
                    <option value="total_pair_tasks">Pair Full Tasks</option>
                    <option value="pair_only_tasks">Pair Only Tasks</option>
                    <option value="total_review_tasks">Review Tasks</option>
                    <option value="total_average_f1">Score F1</option>
                  </select>
                </div>
  
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ordre</label>
                  <select
                    value={draftUiFilters.sortOrder}
                    onChange={(e) => handleDraftChange('sortOrder', e.target.value as 'asc' | 'desc')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="asc">Croissant</option>
                    <option value="desc">Décroissant</option>
                  </select>
                </div>
              </div>
  
              {/* Recherche d'agent */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rechercher un agent</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Entrez l'email d'un agent..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
  
              <p className="text-xs text-gray-500">
                Les changements ci-dessus ne s’appliquent qu’après avoir cliqué sur <span className="font-semibold">“Appliquer”</span>.
              </p>
            </div>
          )}
        </div>
  
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-center space-x-3">
              <span className="w-6 h-6 text-red-500">
                <svg viewBox="0 0 24 24" fill="none"><path d="M12 9v4m0 4h.01M10.29 3.86l-8 14A2 2 0 004 21h16a2 2 0 001.71-3.14l-8-14a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
              <div>
                <h3 className="text-lg font-bold text-red-900 mb-1">Erreur de chargement</h3>
                <p className="text-red-700">{error}</p>
                <button
                  onClick={loadData}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Réessayer
                </button>
              </div>
            </div>
          </div>
        )}
  
        {/* Statistiques globales */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
          <EnhancedStatCard
            title="Total Draw"
            value={globalStats.totalDrawTasks.toLocaleString()}
            icon={BarChart3}
            gradient="from-blue-500 to-cyan-500"
            trend={15}
            description={`${globalStats.totalAgents} agents`}
            percentage={Math.min((globalStats.totalDrawTasks / Math.max(globalStats.totalAllTasks, 1)) * 100, 100)}
          />
  
          <EnhancedStatCard
            title="Total Edit Draw"
            value={globalStats.totalEditDrawTasks.toLocaleString()}
            icon={Target}
            gradient="from-green-500 to-emerald-500"
            trend={8}
            description={`${((globalStats.totalEditDrawTasks / Math.max(globalStats.totalAllTasks, 1)) * 100).toFixed(1)}%`}
            percentage={Math.min((globalStats.totalEditDrawTasks / Math.max(globalStats.totalAllTasks, 1)) * 100, 100)}
          />
  
          <EnhancedStatCard
            title="Total Pair Full"
            value={globalStats.totalPairFullTasks.toLocaleString()}
            icon={Users}
            gradient="from-purple-500 to-pink-500"
            trend={12}
            description="Pair complètes"
            percentage={Math.min((globalStats.totalPairFullTasks / Math.max(globalStats.totalAllTasks, 1)) * 100, 100)}
          />
  
          <EnhancedStatCard
            title="Total Pair Only"
            value={globalStats.totalPairOnlyTasks.toLocaleString()}
            icon={Zap}
            gradient="from-orange-500 to-red-500"
            trend={-3}
            description="Pair uniquement"
            percentage={Math.min((globalStats.totalPairOnlyTasks / Math.max(globalStats.totalAllTasks, 1)) * 100, 100)}
          />
  
          <EnhancedStatCard
            title="Total Review"
            value={globalStats.totalReviewTasks.toLocaleString()}
            icon={CheckCircle}
            gradient="from-indigo-500 to-purple-500"
            trend={20}
            description="Révisions"
            percentage={Math.min((globalStats.totalReviewTasks / Math.max(globalStats.totalAllTasks, 1)) * 100, 100)}
          />
        </div>
  
        {/* Tableau des agents */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Statistiques par Agent</h3>
            <span className="text-sm text-gray-500">
              {totalRows} agent(s) • Page {currentPage}/{totalPages}
            </span>
          </div>
  
          {combinedData.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                {/* Conteneur à hauteur fixe pour scroll intérieur vertical */}
                <div className="max-h-96 overflow-y-auto rounded-lg border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Agent</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Draw</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Edit Draw</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Pair Full</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Pair Only</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Review</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Total</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Score Draw</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Score Pair</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-700">Score F1</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((agent) => (
                        <tr key={agent.email} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-xs">
                                  {agent.email.match(/agent-(\d+)/)?.[1] || agent.email.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-900">{agent.email}</span>
                                {/* Indicateur QA */}
                                {(agent as any).performance && (
                                  <div className="text-xs text-green-600">
                                    ✓ Données QA disponibles
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-bold">
                              <BarChart3 className="w-3 h-3" />
                              {agent.total_draw_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full font-bold">
                              <Target className="w-3 h-3" />
                              {agent.total_match_draw_edit_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-bold">
                              <Users className="w-3 h-3" />
                              {agent.total_pair_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full font-bold">
                              <Zap className="w-3 h-3" />
                              {agent.pair_only_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full font-bold">
                              <CheckCircle className="w-3 h-3" />
                              {agent.total_review_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            <span className="text-lg font-bold text-gray-900">
                              {agent.total_all_tasks.toLocaleString()}
                            </span>
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            {(agent as any).performance ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                                (agent as any).performance.average_draw_score >= 90 ? 'bg-green-100 text-green-800' :
                                (agent as any).performance.average_draw_score >= 80 ? 'bg-blue-100 text-blue-800' :
                                (agent as any).performance.average_draw_score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                <Star className="w-3 h-3" />
                                {(agent as any).performance.average_draw_score.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            {(agent as any).performance ? (
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                                (agent as any).performance.average_pair_score >= 90 ? 'bg-green-100 text-green-800' :
                                (agent as any).performance.average_pair_score >= 80 ? 'bg-blue-100 text-blue-800' :
                                (agent as any).performance.average_pair_score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                <Users className="w-3 h-3" />
                                {(agent as any).performance.average_pair_score.toFixed(1)}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
  
                          <td className="px-4 py-3 text-center">
                            {(agent as any).performance ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                                  (agent as any).performance.total_average_f1 >= 90 ? 'bg-green-100 text-green-800' :
                                  (agent as any).performance.total_average_f1 >= 80 ? 'bg-blue-100 text-blue-800' :
                                  (agent as any).performance.total_average_f1 >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  <Award className="w-3 h-3" />
                                  {(agent as any).performance.total_average_f1.toFixed(1)}
                                </span>
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-2 h-2 ${
                                        i < Math.floor(((agent as any).performance.total_average_f1 || 0) / 20)
                                          ? 'text-yellow-500 fill-current'
                                          : 'text-gray-300'
                                      }`}
                                    />
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
  
              {/* Pagination Controls */}
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                >
                  ← Précédent
                </button>
  
                <div className="text-sm text-gray-600">
                  {((currentPage - 1) * PAGE_SIZE + 1)}–{Math.min(currentPage * PAGE_SIZE, totalRows)} / {totalRows}
                </div>
  
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-50 hover:bg-gray-50"
                >
                  Suivant →
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Database className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-500">Aucune donnée trouvée</p>
              <p className="text-gray-400">Ajustez vos filtres ou vérifiez la période sélectionnée puis cliquez sur “Appliquer”.</p>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  export default TotalTasksView;
  